/**
 * 各页面模板的「内置默认背景 SVG」。
 *
 * 设计约定：
 * - 仅使用 SMIL（<animate>/<animateTransform>）做动画；不使用 <style>/class/@keyframes/CSS 动画。
 * - 颜色通过内联 style 承载主题 CSS 变量（var(--c-*)），并带 fallback 色，
 *   因此背景会随纪念馆所选的配色主题自动变色；后台未加载主题时也能显示。
 * - 数据库 templates.bg_svg 仅存储运营在后台覆盖的值；为空时前端回落到这里的默认值。
 */

export const BUILTIN_BG: Record<string, string> = {
  // 经典追思 —— 清淡远山薄雾 + 淡阳呼吸 + 薄云缓漂
  classic: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
  <rect width="800" height="600" style="fill:var(--c-bg-grad,#eef0f2)" opacity="0.45"/>
  <path d="M0 470 Q220 410 420 450 T800 430 V600 H0 Z" style="fill:var(--c-muted,#9a958c)" opacity="0.10"/>
  <path d="M0 510 Q260 460 520 495 T800 480 V600 H0 Z" style="fill:var(--c-text,#1c1917)" opacity="0.06"/>
  <circle cx="650" cy="120" r="48" style="fill:var(--c-accent-soft,#ffd894)" opacity="0.55">
    <animate attributeName="opacity" values="0.35;0.6;0.35" dur="10s" repeatCount="indefinite"/>
  </circle>
  <g style="fill:var(--c-surface,#ffffff)">
    <ellipse cx="220" cy="150" rx="120" ry="24" opacity="0.5">
      <animate attributeName="cx" values="220;640;220" dur="55s" repeatCount="indefinite"/>
    </ellipse>
    <ellipse cx="540" cy="110" rx="90" ry="18" opacity="0.35">
      <animate attributeName="cx" values="540;120;540" dur="47s" repeatCount="indefinite"/>
    </ellipse>
  </g>
</svg>`,

  // 中式古典 —— 宣纸底 + 极淡墨竹摇曳
  chinese: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
  <rect width="800" height="600" style="fill:var(--c-bg,#faf9f7)" opacity="0.5"/>
  <g opacity="0.14" style="stroke:var(--c-text,#1c1917)" stroke-width="7" fill="none" stroke-linecap="round">
    <path d="M170 600 Q165 380 172 180">
      <animateTransform attributeName="transform" type="rotate" values="-1.5 170 600;1.5 170 600;-1.5 170 600" dur="9s" repeatCount="indefinite"/>
    </path>
    <path d="M150 600 Q150 420 150 260">
      <animateTransform attributeName="transform" type="rotate" values="1.5 150 600;-1.5 150 600;1.5 150 600" dur="12s" repeatCount="indefinite"/>
    </path>
    <path d="M630 600 Q636 400 628 220">
      <animateTransform attributeName="transform" type="rotate" values="1.5 630 600;-1.5 630 600;1.5 630 600" dur="11s" repeatCount="indefinite"/>
    </path>
  </g>
  <g opacity="0.10" style="fill:var(--c-muted,#9a958c)">
    <ellipse cx="182" cy="240" rx="26" ry="7" transform="rotate(-30 182 240)"/>
    <ellipse cx="640" cy="280" rx="26" ry="7" transform="rotate(30 640 280)"/>
  </g>
</svg>`,

  // 暗色守夜 —— 深蓝夜空 + 星点闪烁 + 流萤缓移
  vigil: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
  <rect width="800" height="600" style="fill:var(--c-bg,#141a24)" opacity="0.6"/>
  <g style="fill:var(--c-surface,#e8eaed)">
    <circle cx="120" cy="90" r="2"><animate attributeName="opacity" values="0.2;1;0.2" dur="3.5s" repeatCount="indefinite"/></circle>
    <circle cx="300" cy="140" r="1.6"><animate attributeName="opacity" values="0.3;0.9;0.3" dur="2.6s" repeatCount="indefinite"/></circle>
    <circle cx="500" cy="80" r="2.2"><animate attributeName="opacity" values="0.2;1;0.2" dur="4s" repeatCount="indefinite"/></circle>
    <circle cx="680" cy="160" r="1.8"><animate attributeName="opacity" values="0.3;0.8;0.3" dur="3s" repeatCount="indefinite"/></circle>
    <circle cx="420" cy="210" r="1.5"><animate attributeName="opacity" values="0.2;0.9;0.2" dur="2.8s" repeatCount="indefinite"/></circle>
  </g>
  <circle cx="400" cy="300" r="5" style="fill:var(--c-accent-soft,#3a2f1c)">
    <animate attributeName="opacity" values="0.2;0.9;0.2" dur="4s" repeatCount="indefinite"/>
    <animateTransform attributeName="transform" type="translate" values="0 0;70 -40;-40 30;0 0" dur="19s" repeatCount="indefinite"/>
  </circle>
  <circle cx="200" cy="430" r="4" style="fill:var(--c-accent-soft,#3a2f1c)">
    <animate attributeName="opacity" values="0.15;0.8;0.15" dur="5s" repeatCount="indefinite"/>
    <animateTransform attributeName="transform" type="translate" values="0 0;-50 -30;40 20;0 0" dur="22s" repeatCount="indefinite"/>
  </circle>
</svg>`,

  // 现代卡片 —— 清淡晨雾 + 远山 + 淡阳
  cards: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
  <rect width="800" height="600" style="fill:var(--c-bg-grad,#e6efec)" opacity="0.45"/>
  <path d="M0 460 Q240 400 460 445 T800 425 V600 H0 Z" style="fill:var(--c-muted,#6b857e)" opacity="0.10"/>
  <path d="M0 505 Q260 465 520 495 T800 485 V600 H0 Z" style="fill:var(--c-text,#1e2f2b)" opacity="0.06"/>
  <g style="fill:var(--c-surface,#ffffff)">
    <rect x="-100" y="300" width="1000" height="60" opacity="0.12">
      <animate attributeName="y" values="300;260;300" dur="16s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0.05;0.14;0.05" dur="16s" repeatCount="indefinite"/>
    </rect>
    <rect x="-100" y="380" width="1000" height="50" opacity="0.10">
      <animate attributeName="y" values="380;350;380" dur="20s" repeatCount="indefinite"/>
    </rect>
  </g>
  <circle cx="120" cy="120" r="34" style="fill:var(--c-accent-soft,#bfe3d8)" opacity="0.5">
    <animate attributeName="opacity" values="0.3;0.55;0.3" dur="10s" repeatCount="indefinite"/>
  </circle>
</svg>`,

  // 简洁极简 —— 近乎纯色 + 一道极淡光线扫过
  minimal: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
  <rect width="800" height="600" style="fill:var(--c-bg,#faf9f7)" opacity="0.25"/>
  <circle cx="400" cy="300" r="120" style="fill:var(--c-accent-soft,#ffd894)" opacity="0.06">
    <animate attributeName="opacity" values="0.04;0.10;0.04" dur="10s" repeatCount="indefinite"/>
  </circle>
  <rect x="-260" y="-100" width="140" height="800" style="fill:var(--c-surface,#ffffff)" opacity="0.05" transform="skewX(-18)">
    <animate attributeName="x" values="-260;1000;-260" dur="15s" repeatCount="indefinite"/>
  </rect>
</svg>`,

  // 灵堂·中堂 —— 陵园晨昏（远山/薄云/淡月呼吸/墓碑/白花/石板径/飘瓣）
  'lingtang-hall': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
  <rect width="800" height="600" style="fill:var(--c-bg-grad,#f2f0ec)" opacity="0.55"/>
  <circle cx="615" cy="125" r="42" style="fill:var(--c-accent-soft,#ffd894)" opacity="0.6">
    <animate attributeName="opacity" values="0.5;0.8;0.5" dur="10s" repeatCount="indefinite"/>
  </circle>
  <g style="fill:var(--c-surface,#ffffff)">
    <ellipse cx="250" cy="140" rx="130" ry="22" opacity="0.45">
      <animate attributeName="cx" values="250;640;250" dur="52s" repeatCount="indefinite"/>
    </ellipse>
    <ellipse cx="560" cy="95" rx="92" ry="16" opacity="0.7">
      <animate attributeName="cx" values="560;120;560" dur="44s" repeatCount="indefinite"/>
    </ellipse>
  </g>
  <path d="M0 430 Q200 380 420 408 T800 398 V600 H0 Z" style="fill:var(--c-muted,#6b857e)" opacity="0.18"/>
  <path d="M0 472 Q260 432 520 460 T800 450 V600 H0 Z" style="fill:var(--c-text,#1c1917)" opacity="0.10"/>
  <path d="M0 540 Q220 520 440 535 T800 525 V600 H0 Z" style="fill:var(--c-muted,#6b857e)" opacity="0.14"/>
  <g style="stroke:var(--c-text,#1c1917)" stroke-width="2.5" fill="none" opacity="0.22">
    <rect x="648" y="430" width="58" height="92" rx="26"/>
    <line x1="648" y1="462" x2="706" y2="462"/>
  </g>
  <path d="M360 600 L420 600 L470 472 L330 472 Z" style="fill:var(--c-surface,#ffffff)" opacity="0.10"/>
  <g style="fill:var(--c-surface,#ffffff)" opacity="0.55">
    <circle cx="300" cy="556" r="4"/>
    <circle cx="316" cy="566" r="3"/>
    <circle cx="286" cy="566" r="3"/>
    <circle cx="520" cy="558" r="4"/>
    <circle cx="504" cy="567" r="3"/>
  </g>
  <g style="fill:var(--c-accent-soft,#ffd894)" opacity="0.6">
    <path d="M210 130 q7 -9 14 0 q-7 9 -14 0 Z">
      <animateTransform attributeName="transform" type="translate" values="0 0;25 390" dur="11s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0;0.6;0" dur="11s" repeatCount="indefinite"/>
    </path>
  </g>
</svg>`,

  // 灵堂·守夜 —— 夜空星点 + 极缓烛光呼吸
  'lingtang-night': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
  <rect width="800" height="600" style="fill:var(--c-bg,#16110b)" opacity="0.65"/>
  <circle cx="400" cy="170" r="120" style="fill:var(--c-accent-soft,#3d2c14)" opacity="0.22">
    <animate attributeName="opacity" values="0.12;0.3;0.12" dur="10s" repeatCount="indefinite"/>
    <animate attributeName="r" values="108;132;108" dur="10s" repeatCount="indefinite"/>
  </circle>
  <g style="fill:var(--c-surface,#f4ede1)">
    <circle cx="150" cy="120" r="2"><animate attributeName="opacity" values="0.2;1;0.2" dur="3.6s" repeatCount="indefinite"/></circle>
    <circle cx="640" cy="100" r="2.2"><animate attributeName="opacity" values="0.3;0.9;0.3" dur="4.2s" repeatCount="indefinite"/></circle>
    <circle cx="700" cy="240" r="1.6"><animate attributeName="opacity" values="0.2;0.8;0.2" dur="3s" repeatCount="indefinite"/></circle>
    <circle cx="120" cy="300" r="1.8"><animate attributeName="opacity" values="0.3;0.9;0.3" dur="3.4s" repeatCount="indefinite"/></circle>
    <circle cx="520" cy="160" r="1.5"><animate attributeName="opacity" values="0.2;0.8;0.2" dur="2.8s" repeatCount="indefinite"/></circle>
  </g>
  <circle cx="400" cy="170" r="6" style="fill:var(--c-accent,#cb8a30)">
    <animate attributeName="opacity" values="0.4;1;0.4" dur="6s" repeatCount="indefinite"/>
  </circle>
</svg>`,

  // 灵堂·花祭 —— 暖调花瓣缓飘
  'lingtang-wreath': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
  <rect width="800" height="600" style="fill:var(--c-bg-grad,#fdf3e3)" opacity="0.4"/>
  <circle cx="400" cy="300" r="140" style="fill:var(--c-accent,#e08c1a)" opacity="0.05">
    <animate attributeName="opacity" values="0.03;0.09;0.03" dur="10s" repeatCount="indefinite"/>
  </circle>
  <g style="fill:var(--c-accent-soft,#ffe6b0)">
    <path d="M200 -20 q8 -10 16 0 q-8 10 -16 0 Z" opacity="0.7">
      <animateTransform attributeName="transform" type="translate" values="0 0;60 640" dur="12s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0;0.7;0" dur="12s" repeatCount="indefinite"/>
    </path>
    <path d="M420 -20 q7 -9 14 0 q-7 9 -14 0 Z" opacity="0.6">
      <animateTransform attributeName="transform" type="translate" values="0 0;-30 640" dur="9s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0;0.6;0" dur="9s" repeatCount="indefinite"/>
    </path>
    <path d="M600 -20 q9 -11 18 0 q-9 11 -18 0 Z" opacity="0.65">
      <animateTransform attributeName="transform" type="translate" values="0 0;30 640" dur="14s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0;0.65;0" dur="14s" repeatCount="indefinite"/>
    </path>
  </g>
</svg>`,
};

/** 取最终使用的背景 SVG：优先用后台覆盖值，否则回落内置默认 */
export function resolveBg(slug: string, bgSvg?: string | null): string {
  const s = (bgSvg ?? '').trim();
  if (s) return s;
  return BUILTIN_BG[slug] ?? '';
}

/** 轻量净化：仅允许 <svg> 根节点，剥离 <script> 与事件属性（后台专属、纵深防御） */
export function sanitizeSvg(svg: string): string {
  if (!svg) return '';
  let s = svg.trim();
  if (!/^<svg[\s>]/i.test(s)) return ''; // 根节点必须是 <svg>
  s = s.replace(/<script[\s\S]*?<\/script>/gi, '');
  s = s.replace(/\s+on\w+\s*=\s*("([^"]*)"|'([^']*)'|[^\s>]+)/gi, '');
  s = s.replace(/(href|xlink:href)\s*=\s*("|')\s*javascript:[^"']*\2/gi, '$1=$2#$2');
  return s;
}
